import arrayToObject from 'helpers/arrayToObject';
import colors from 'styles/themes/colors';
import formatNumber from 'helpers/formatNumber';
import getEntityKey from 'helpers/getEntityKey';

import existsIn from './existsIn';
import getBreakdownLabel from './getBreakdownLabel';
import getClosestDate from './getClosestDate';
import { ICustomer } from 'redux/reducers/customers';
import { ITrendWithBreakdown } from './../Overall3rdParty/types';
import { ISalesType, ITrendDataItem, IDataTogglePairs } from '../types';

interface IToggleData {
  trends: ITrendWithBreakdown[];
  period: Date | null;
  salesType: ISalesType;
  customers: ICustomer[] | null;
  colorScale: any;
  customerFilter?: any;
  accessTrends?: ITrendWithBreakdown[] | null;
  compareMode?: any;
  useBreakdownLabel?: boolean;
  useShortValue?: boolean;
}

function getDataTogglePairs({
  trends,
  period,
  salesType,
  customers,
  colorScale,
  customerFilter,
  accessTrends,
  compareMode,
  useBreakdownLabel,
  useShortValue,
}: IToggleData): undefined | IDataTogglePairs[] {
  if (!trends || !period) return undefined;

  const { label, split } = salesType;
  const color = colorScale;

  // Does the selected period exist in the data?
  const periodExists = existsIn(trends, period);

  if (periodExists) {
    const response = trends.map((trend: ITrendWithBreakdown) => {
      const { breakdown = undefined, data } = trend;
      const accessData =
        accessTrends && compareMode
          ? accessTrends.filter((at: any) => at.entity.id === trend.entity.id)
          : null;

      const d = data.find(
        (d: ITrendDataItem) => d.date.getTime() === Number(period)
      );
      const value = split ? d?.[`${split}_value`] : d?.value;
      return {
        label: useBreakdownLabel
          ? breakdown
          : (customerFilter || breakdown) && !accessTrends
          ? getBreakdownLabel(customerFilter || breakdown, customers)
          : label,
        value:
          accessTrends || useShortValue
            ? formatNumber(value, 'number', true)
            : formatNumber(value),
        key: getEntityKey(trend),
        dataColor: color(trend),
        selector: getEntityKey(trend),
        rawValue: d?.value,
        accessData: accessData
          ? getAccessData(accessData, period, true, color(trend))
          : null,
      };
    });

    const hasAnyTrendData = response.some(
      (r: IDataTogglePairs) => r.rawValue !== null
    );

    return !compareMode && accessTrends
      ? [...response, ...getAccessData(accessTrends, period, hasAnyTrendData)]
      : response;
  }

  const closestDate = getClosestDate(period, trends);

  const response = trends.map((trend: ITrendWithBreakdown) => {
    const { breakdown = undefined, data } = trend;
    const accessData =
      accessTrends && compareMode
        ? accessTrends.filter((at: any) => at.entity.id === trend.entity.id)
        : null;

    const d = data.find(
      (d: ITrendDataItem) => d.date.getTime() === closestDate?.getTime()
    );

    const value = split ? d?.[`${split}_value`] : d?.value;

    return {
      label:
        (customerFilter || breakdown) && !accessTrends
          ? getBreakdownLabel(customerFilter || breakdown, customers)
          : label,
      value: accessTrends
        ? formatNumber(value, 'number', true)
        : formatNumber(value),
      key: getEntityKey(trend),
      dataColor: color(trend),
      selector: getEntityKey(trend),
      rawValue: d?.value,
      accessData: accessData
        ? getAccessData(accessData, period, true, color(trend))
        : null,
    };
  });

  const hasAnyTrendData = response.some(
    (r: IDataTogglePairs) => r.rawValue !== null
  );

  return !compareMode && accessTrends
    ? [...response, ...getAccessData(accessTrends, period, hasAnyTrendData)]
    : response;
}

const getAccessTypesData = (
  accessTrends: ITrendWithBreakdown[],
  period: Date | null
): {
  accessBreakdownTrends: any;
  premiumDataItem: any;
  freemiumDataItem: any;
  naDataItem: any;
} => {
  const accessBreakdownTrends = arrayToObject(accessTrends, 'breakdown');
  const premiumDataItem = accessBreakdownTrends['premium']?.data.find(
    (d: ITrendDataItem) => d.date.getTime() === Number(period)
  );
  const freemiumDataItem = accessBreakdownTrends['freemium']?.data.find(
    (d: ITrendDataItem) => d.date.getTime() === Number(period)
  );
  const naDataItem = accessBreakdownTrends['na']?.data.find(
    (d: ITrendDataItem) => d.date.getTime() === Number(period)
  );
  return {
    accessBreakdownTrends,
    premiumDataItem,
    freemiumDataItem,
    naDataItem,
  };
};

function getAccessData(
  accessTrends: ITrendWithBreakdown[],
  period: Date | null,
  trendHasData = true,
  color?: string
): any {
  const {
    accessBreakdownTrends,
    premiumDataItem,
    freemiumDataItem,
    naDataItem,
  } = getAccessTypesData(accessTrends, period);
  return [
    {
      label: 'Premium',
      value: formatNumber(
        trendHasData ? premiumDataItem?.value : null,
        'number',
        true
      ),
      key: getEntityKey(accessBreakdownTrends['premium']),
      dataColor: color || colors.NightBlue,
      selector: getEntityKey(accessBreakdownTrends['premium']),
      rawValue: premiumDataItem?.value,
    },
    {
      label: 'Freemium',
      value: formatNumber(
        trendHasData ? freemiumDataItem?.value : null,
        'number',
        true
      ),
      key: getEntityKey(accessBreakdownTrends['freemium']),
      dataColor: color || colors.NightBlue,
      selector: getEntityKey(accessBreakdownTrends['freemium']),
      rawValue: freemiumDataItem?.value,
    },
    {
      label: 'Source n/a',
      value: formatNumber(
        trendHasData ? naDataItem?.value : null,
        'number',
        true
      ),
      key: getEntityKey(accessBreakdownTrends['na']),
      dataColor: color || colors.NightBlue,
      selector: getEntityKey(accessBreakdownTrends['na']),
      rawValue: naDataItem?.value,
    },
  ];
}

export default getDataTogglePairs;
